import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useMemo, useRef, useState } from 'react';
import { getTestIds } from '../../utils/TestIds';
import { Autocomplete } from '../Autocomplete/Autocomplete';
import { useBiAction } from '../BiTracking';
import { Button } from '../Button/Button';
import { Checkbox } from '../Checkbox/Checkbox';
import { useDevice } from '../Device';
import { Box } from '../Layout';
import styles from './MultiAutocomplete.module.scss';
import { MultiAutocompleteChip } from './MultiAutocompleteChip';
import { MultiAutocompleteMobile } from './MultiAutocompleteMobile';
export const GetMultiAutocompleteTID = (parentTestId)=>getTestIds({
        parentTestId
    });
export function MultiAutocomplete(props) {
    const { options, value, onApply, getOptionId, getOptionLabel, getOptionDisabled, renderOption, onDraftChange, isLoading, isError, errorText, label, placeholder, applyLabel, noOptionsText, testId, bi } = props;
    const { MultiAutocompleteTestIds } = useMemo(()=>GetMultiAutocompleteTID(testId), [
        testId
    ]);
    const logBiAction = useBiAction();
    const { isMobileDevice } = useDevice();
    const resolvedTestId = testId !== null && testId !== void 0 ? testId : '';
    const [draft, setDraft] = useState(value);
    const [inputValue, setInputValue] = useState('');
    const draftRef = useRef(draft);
    // WHY: on mobile every popover prop (renderOption, getOptionDisabled, onChange, the Apply
    // footer) is snapshotted into the BottomSheet at open time, so those closures must read the
    // latest draft via ref rather than the open-time draft value.
    // eslint-disable-next-line custom/no-useref-for-prop-mirror
    draftRef.current = draft;
    const isInDraft = (option)=>draftRef.current.some((o)=>getOptionId(o) === getOptionId(option));
    const logOptionBi = (optionId, wasSelected)=>{
        if (bi === null || bi === void 0 ? void 0 : bi.name) {
            logBiAction({
                entityType: 'multi_autocomplete',
                entityName: bi.name,
                actionType: wasSelected ? 'off' : 'on',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    option_id: optionId,
                    test_id: resolvedTestId
                })
            });
        }
    };
    const toggle = (option)=>{
        const wasSelected = isInDraft(option);
        logOptionBi(getOptionId(option), wasSelected);
        const next = wasSelected ? draftRef.current.filter((o)=>getOptionId(o) !== getOptionId(option)) : [
            ...draftRef.current,
            option
        ];
        setDraft(onDraftChange ? onDraftChange(next, option) : next);
    };
    const apply = ()=>{
        if (bi === null || bi === void 0 ? void 0 : bi.name) {
            logBiAction({
                entityType: 'multi_autocomplete',
                entityName: bi.name,
                actionType: 'select',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: resolvedTestId
                })
            });
        }
        onApply(draftRef.current);
    };
    const removeChip = (id)=>{
        logOptionBi(id, true);
        setDraft(draftRef.current.filter((o)=>getOptionId(o) !== id));
        onApply(value.filter((o)=>getOptionId(o) !== id));
    };
    if (isMobileDevice) {
        return /*#__PURE__*/ _jsx(MultiAutocompleteMobile, _object_spread({}, props));
    }
    return /*#__PURE__*/ _jsx(Autocomplete, {
        // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- WHY: MultiAutocomplete emits its own multi_autocomplete BI (option on/off in toggle, select in apply); instrumenting the wrapped Autocomplete would double-fire.
        noBi: true,
        multiple: true,
        disableCloseOnSelect: true,
        disableSelectedHighlight: true,
        testId: MultiAutocompleteTestIds.Root(),
        options: options,
        value: value,
        noOptionsText: noOptionsText !== null && noOptionsText !== void 0 ? noOptionsText : 'No options',
        loading: isLoading,
        error: isError,
        helperText: isError ? errorText : undefined,
        getOptionLabel: getOptionLabel,
        getOptionDisabled: (option)=>{
            var _ref;
            return (_ref = getOptionDisabled === null || getOptionDisabled === void 0 ? void 0 : getOptionDisabled(option, draftRef.current)) !== null && _ref !== void 0 ? _ref : false;
        },
        inputValue: inputValue,
        onInputChange: (_event, nextInputValue)=>setInputValue(nextInputValue),
        onOpen: ()=>{
            setDraft(value);
            setInputValue('');
        },
        onChange: (_event, _changedValue, reason, detail)=>{
            if (reason === 'clear') {
                setDraft([]);
                onApply([]);
                return;
            }
            if (reason !== 'select-option' && reason !== 'remove-option') return;
            if (detail === null || detail === void 0 ? void 0 : detail.option) toggle(detail.option);
        },
        renderOption: (option)=>{
            const selected = isInDraft(option);
            return renderOption ? renderOption(option, {
                selected
            }) : /*#__PURE__*/ _jsx(Checkbox, {
                // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- WHY: toggle() logs the option on/off BI; instrumenting this Checkbox would double-fire.
                noBi: true,
                checked: selected,
                label: getOptionLabel(option),
                testId: MultiAutocompleteTestIds.Option(getOptionId(option))
            });
        },
        renderTags: (committed)=>committed.map((option)=>{
                const id = getOptionId(option);
                return /*#__PURE__*/ _jsx(MultiAutocompleteChip, {
                    label: getOptionLabel(option),
                    onRemove: ()=>removeChip(id),
                    testId: MultiAutocompleteTestIds.Chip(id)
                }, id);
            }),
        textFieldProps: {
            label,
            placeholder: value.length > 0 ? '' : placeholder,
            alwaysShrinkLabel: true
        },
        popoverFooter: /*#__PURE__*/ _jsx(Box, {
            className: styles.applyFooter,
            children: /*#__PURE__*/ _jsx(Button, {
                // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- WHY: apply() logs the select BI; instrumenting this Button would double-fire.
                noBi: true,
                fluid: true,
                type: "button",
                onMouseDown: apply,
                disabled: isLoading,
                testId: MultiAutocompleteTestIds.Apply(),
                children: applyLabel !== null && applyLabel !== void 0 ? applyLabel : 'Apply'
            })
        })
    });
}
